import { Inject, Injectable } from '@angular/core';
import { KieneApi, KieneBackendApiService } from '../services/backend-api/kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../kiene-core.config';
import { HttpParams } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { Bestandsbuch, BestandsbuchAngewandt } from './classes/bestandsbuch';
import { BestandsbuchArtikel } from './classes/bestandsbuch-artikel';
import { LocalStorageService } from '../services/local-storage/local-storage.service';
import { ConfirmDialogInput, ConfirmDialogResult } from '../components/dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../components/dialogs/confirm-dialog/confirm-dialog.component';
import { MessageService } from '../services/message-service/message.service';
import { MatDialog } from '@angular/material/dialog';
import { param } from 'cypress/types/jquery';

@Injectable({ providedIn: 'root' })
export class BestandsbuchService {
    constructor(
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl,
        private readonly api: KieneBackendApiService,
        private readonly lss: LocalStorageService,
        private readonly messageService: MessageService,
        public dialog: MatDialog,
    ) {}

    getBestandsbuch(bestandsbuch_id: number) {
        const params = new HttpParams().set('bestandsbuch_id', bestandsbuch_id.toString());
        return this.api.get(`${this.apiBaseUrl}bestandsbuch/read_one_kundenportal.php`, params);
    }

    updateBestandsbuch(bestandsbuch: Bestandsbuch) {
        return this.api.post(`${this.apiBaseUrl}bestandsbuch/update_kundenportal.php`, bestandsbuch, null);
    }

    getBestandsbuecher(offset: number, limit: number, kunde_id?: number): Observable<KieneApi> {
        let params = new HttpParams().append('orderby', 'status_sortierung asc');
        params = params.set('offset', offset);
        params = params.set('limit', limit);
        if (kunde_id) {
            params = params.append('kunde_id', kunde_id);
        }
        return this.api.getAll(`${this.apiBaseUrl}bestandsbuch/read_kundenportal.php`, params);
    }

    // Recht: 4427
    bestandsbuchTrotzdemAbschliessen(bestandsbuch: Bestandsbuch) {
        if (this.lss.hasPermission(4427)) {
            return this.api.post(`${this.apiBaseUrl}bestandsbuch/trotzdem_abschliessen.php`, bestandsbuch, null);
        } else {
            return of();
        }
    }

    bestandsbuchAbschliessen(bestandsbuch: Bestandsbuch, force: boolean) {
        if (force) {
            bestandsbuch.force = 1;
        } else {
            bestandsbuch.force = 0;
        }
        return this.api.post(`${this.apiBaseUrl}bestandsbuch/abschliessen_bestandsbuch.php`, bestandsbuch, null);
    }

    getBestandsbuchArtikel(bestandsbuch_artikel_id: number) {
        const params = new HttpParams().set('bestandsbuch_artikel_id', bestandsbuch_artikel_id);
        return this.api.get(`${this.apiBaseUrl}bestandsbuch_artikel/read_one_kundenportal.php`, params);
    }

    updateBestandsbuchArtikel(bestandsbuch_artikel: BestandsbuchArtikel) {
        return this.api.post(
            `${this.apiBaseUrl}bestandsbuch_artikel/update_kundenportal.php`,
            bestandsbuch_artikel,
            null,
        );
    }

    alleArtikelProtokollieren(bestandsbuch: Bestandsbuch) {
        return this.api.post(`${this.apiBaseUrl}bestandsbuch/alle_angewendet_kundenportal.php`, bestandsbuch, null);
    }

    alleBestandsbuecherAbschliessen(kunde_id?: number): Observable<boolean> {
        const sub = new Subject<boolean>();
        let params = new HttpParams();
        if (kunde_id) {
            params = params.set('kunde_id', kunde_id);
        }
        this.api.getAll(this.apiBaseUrl + 'bestandsbuch/alle_angewendet_bestandsbuecher.php', params).subscribe({
            next: response => {
                if (response.count > 0) {
                    const nichtAbschliessbar = this.getNichtAbschliessbar(response.records);
                    if (nichtAbschliessbar > 0) {
                        const inp = new ConfirmDialogInput();
                        inp.headline = 'Trotzdem abschließen?';
                        inp.text =
                            nichtAbschliessbar +
                            ' Bestandsbuch(er) erfüllen nicht alle Voraussetzungen zum Abschließen. Sollen diese auch abgeschlossen werden?';
                        inp.okButton = 'Ja';
                        inp.showOkButton2 = true;
                        inp.okButton2 = 'Nein';
                        inp.cancelButton = 'Abbruch';
                        this.dialog
                            .open(ConfirmDialogComponent, { data: inp })
                            .afterClosed()
                            .subscribe({
                                next: (result: ConfirmDialogResult) => {
                                    if (result?.ok) {
                                        if (result.confirmed) {
                                            this.bestandsbuecherAbschliessen(true, kunde_id).subscribe({
                                                next: (res: boolean) => {
                                                    sub.next(res);
                                                },
                                            });
                                        } else if (result.confirmed2) {
                                            this.bestandsbuecherAbschliessen(false, kunde_id).subscribe({
                                                next: (res: boolean) => {
                                                    sub.next(res);
                                                },
                                            });
                                        }
                                    } else {
                                        sub.next(false);
                                    }
                                },
                            });
                    } else {
                        this.bestandsbuecherAbschliessen(false, kunde_id).subscribe({
                            next: (res: boolean) => {
                                sub.next(res);
                            },
                        });
                    }
                } else {
                    this.messageService.infoMessage('Keine Bestandsbücher zum Abschließen gefunden!');
                    sub.next(false);
                }
            },
        });
        return sub;
    }

    private bestandsbuecherAbschliessen(force: boolean, kunde_id: number): Observable<boolean> {
        const sub = new Subject<boolean>();
        let params = new HttpParams();
        if (kunde_id > 0) {
            params = params.append('kunde_id', kunde_id);
        }
        this.api
            .post(this.apiBaseUrl + 'bestandsbuch/abschliessen_bestandsbuecher.php', { force: force ? 1 : 0 }, params)
            .subscribe({
                next: () => {
                    this.messageService.infoMessage('Bestandsbücher wurden erfolgreich abgeschlossen!');
                    sub.next(true);
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                    sub.next(false);
                },
            });
        return sub;
    }

    private getNichtAbschliessbar(buecher: BestandsbuchAngewandt[]): number {
        let nichtAbschliessbar = 0;
        for (const b of buecher) {
            if (Number(b.abschliessbar) === 0) {
                nichtAbschliessbar++;
            }
        }
        return nichtAbschliessbar;
    }
}
